import { useSubscription } from "@/hooks/useSubscription";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, XCircle, CreditCard } from "lucide-react";

export function SubscriptionBanner() {
  const { subscription, isLoading } = useSubscription();

  if (isLoading || !subscription) return null;

  const { status, daysRemaining } = subscription;

  // Don't show banner for active/trial subscriptions with more than 7 days
  if (['trial', 'active'].includes(status) && daysRemaining > 7) {
    return null;
  }

  // Expiring soon banner (7 days or less)
  if (status === 'expiring_soon') {
    return (
      <Alert className="border-amber-500/50 bg-amber-500/10 mb-4">
        <Clock className="h-4 w-4 text-amber-500" />
        <AlertTitle className="text-amber-600">Subscription Expiring Soon</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            Your subscription expires in <strong>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''}</strong>. 
            Renew now to avoid interruption.
          </span>
          <Link to="/business/billing">
            <Button size="sm" variant="default" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Renew Now
            </Button>
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  // Expired banner
  if (status === 'expired') {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Subscription Expired</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            Your subscription has expired. You can view data but cannot add or edit. 
            Renew to continue using all features.
          </span>
          <Link to="/business/billing">
            <Button size="sm" variant="outline" className="gap-2 border-destructive text-destructive hover:bg-destructive/10">
              <CreditCard className="h-4 w-4" />
              Renew Now
            </Button>
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  // Grace period banner
  if (status === 'grace') {
    return (
      <Alert variant="destructive" className="mb-4 border-orange-500 bg-orange-500/10">
        <AlertTriangle className="h-4 w-4 text-orange-500" />
        <AlertTitle className="text-orange-600">Grace Period - {daysRemaining} Days Left</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span className="text-orange-700">
            Your subscription has expired and you're in a 7-day grace period. 
            <strong> Pay now to avoid losing access to all tools.</strong>
          </span>
          <Link to="/business/billing">
            <Button size="sm" className="gap-2 bg-orange-500 hover:bg-orange-600">
              <CreditCard className="h-4 w-4" />
              Pay Now
            </Button>
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  // Suspended banner
  if (status === 'suspended') {
    return (
      <Alert variant="destructive" className="mb-4">
        <XCircle className="h-4 w-4" />
        <AlertTitle>Account Suspended</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>
            Your subscription has been suspended due to non-payment. 
            All tools are disabled. Renew now to restore access. 
            <strong> Your data is safe and will not be deleted.</strong>
          </span>
          <Link to="/business/billing">
            <Button size="sm" variant="destructive" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Restore Access
            </Button>
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}
